import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  SiteConfigContext,
  DataServiceContext,
  useIsSmartPageBookAppointmentEnabled,
  useCurrentLocale,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  DATA_SOURCE_KEY_MAPPING,
  getDataSourceByKey,
  getWebsiteSourceDataByKey,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
  getTimeZoneOffset,
} from '@bingads-webui-campaign-react/smart-component-util';
import { MailService } from '@bingads-webui-campaign-react/smart-page-data-service';

import { SmartActionButtonView } from './smart-action-button-view';
import { SmartMainActionButtonView } from './smart-main-action-button-view';
import { getActionButtonProps, getIsUsedExternalLink } from './utils/get-action-button-props';
import { proxyReadHoursConfig } from './utils/proxy-read-hours-config';

export const SmartActionButtonConnector = ({
  id,
  overrideButtonConfig,
  className,
  useIconButton,
  disabled,
  isEditMode,
}) => {
  const { siteConfig } = useContext(SiteConfigContext);
  const i18n = useContext(I18nContext);
  const { Urls: { SignOutUrl = null } = {} } = useContext(DataServiceContext);
  const userLocale = useCurrentLocale();
  const isSmartPageBookAppointmentEnabled = useIsSmartPageBookAppointmentEnabled();

  const {
    actionType,
    text,
    targetUri,
    linkUrl,
    linkText,
    isFormLink,
    icon,
    componentConfig,
  } = getActionButtonProps(siteConfig, id, overrideButtonConfig, useIconButton);
  const emails = getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.Emails, []);
  const businessName = getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.BusinessName, '');
  const availableBookingHours = proxyReadHoursConfig(siteConfig);
  const timeZoneId = getWebsiteSourceDataByKey(
    siteConfig,
    WEBSITE_SOURCE_DATA_KEY_MAPPING.TimeZoneId
  );
  const customerTimeZoneOffset = getTimeZoneOffset({ timeZoneId });
  const smartPageId = siteConfig.id;
  const landingLink = siteConfig.siteContextInfo ? siteConfig.siteContextInfo.sourceUrl : '';

  const mailService = useMemo(() => MailService({
    location: window.location,
    customerName: businessName,
    customerEmail: emails.length > 0 ? emails[0] : '',
    signOutUrl: SignOutUrl,
  }), [SignOutUrl, emails, businessName]);

  const pilots = useMemo(() => ({
    isSmartPageBookAppointmentEnabled,
  }), [isSmartPageBookAppointmentEnabled]);

  const bookingProps = useMemo(() => ({
    smartPageId,
    availableBookingHours,
    landingLink,
    email: emails.length > 0 ? emails[0] : '',
    isExternalLink: getIsUsedExternalLink(componentConfig),
    businessName,
    customerTimeZoneOffset,
  }), [
    smartPageId, availableBookingHours, landingLink, emails,
    componentConfig, businessName, customerTimeZoneOffset,
  ]);

  const Component = overrideButtonConfig ? SmartActionButtonView : SmartMainActionButtonView;

  return (
    <React.Fragment>
      <Component
        id={id}
        className={className}
        i18n={i18n}
        icon={icon}
        disabled={disabled}
        actionType={actionType}
        text={linkText || i18n.getString(text)}
        target={linkUrl || targetUri}
        useIconButton={useIconButton}
        isFormLink={isFormLink}
        isEditMode={isEditMode}
        mailService={mailService}
        bookingProps={bookingProps}
        userLocale={userLocale}
        pilots={pilots}
      />
    </React.Fragment>
  );
};

SmartActionButtonConnector.propTypes = {
  id: PropTypes.string.isRequired,
  overrideButtonConfig: PropTypes.shape({}),
  className: PropTypes.string,
  useIconButton: PropTypes.bool,
  disabled: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

SmartActionButtonConnector.defaultProps = {
  overrideButtonConfig: null,
  className: '',
  useIconButton: false,
  disabled: false,
  isEditMode: false,
};
